import { useFormContext } from "react-hook-form";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import { usePatientTypes } from "@/api/Patients";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  patientTypeDictionary,
  type IPatientType,
} from "@medoma/models/patients";
import { useLingui } from "@lingui/react/macro";
import { deducedError } from "@/Utils/ErrorUtils";

export type PatientTypeInputFields = {
  patientType: IPatientType;
};

const PatientTypeInput = ({ readonly }: { readonly?: boolean }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<PatientTypeInputFields>();

  const { t } = useLingui();

  const {
    data: availablePatientTypes,
    isPending: isPendingPatientTypes,
    isError: isErrorPatientTypes,
    error: errorPatientTypes,
  } = usePatientTypes();

  if (isPendingPatientTypes) {
    return <Loading message={t`Hämtar patienttyper`} />;
  }

  if (isErrorPatientTypes) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patienttyper.`} ${deducedError(errorPatientTypes)}`}
      />
    );
  }

  const patientTypeOptions = {
    // A disabled radio reports no value at all, so requiring one would leave the
    // form with an error nobody can clear.
    required: {
      value: !readonly,
      message: t`Ange en patienttyp för patienten`,
    },
  };

  // If there is only one patient type, return a hidden input with the value.
  // We can assume that the default patient type is the only one that is available.
  if (availablePatientTypes && availablePatientTypes.length === 1) {
    return (
      <input
        type="hidden"
        value={availablePatientTypes[0]}
        {...register("patientType", patientTypeOptions)}
      />
    );
  }

  return (
    <RadioButtons
      errorMessage={errors.patientType?.message}
      legend={t`Patienttyp`}
      description={readonly ? t`Går inte att ändra` : undefined}
    >
      {availablePatientTypes.length === 0 ? (
        <ErrorMessage
          message={t`Inga patienttyper hittades. Försök igen om en liten stund. Kontakta support om problemet kvarstår.`}
        />
      ) : (
        availablePatientTypes.map((patientType) => (
          <RadioButton
            disabled={readonly}
            label={{ text: t(patientTypeDictionary[patientType]) }}
            value={patientType}
            key={patientType}
            {...register("patientType", patientTypeOptions)}
          />
        ))
      )}
    </RadioButtons>
  );
};

export default PatientTypeInput;
